import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAutoReplyKeywordRule,
  deleteAutoReplyKeywordRule,
  getAutoReplyKeywordRules,
  TAutoReplyKeywordRule,
  TAutoReplyMessageAsset,
  updateAutoReplyKeywordRule,
} from 'api';
import { Button } from 'components/Button';
import { autoReplyMessageStrings } from 'constants/strings';
import { queryKeys } from 'constants/queryKeys';
import {
  useOrganizationId,
  useUnsavedChangesGuard,
} from 'components/OrganizationScope/context';
import useUploadFile from 'hooks/useUploadFile';
import useToast from 'hooks/useToast';
import { TMessageSection } from 'types';
import {
  areMessageAssetSectionsValid,
  areSurveySectionsValid,
  createDefaultAutoReplySections,
  hasFilledReplySection,
} from 'utils/messageSection';
import {
  convertDraftSectionsToMessageSections,
  formatSectionsForAPI,
} from '../SendMessageForAll/utils';
import { AutoReplySectionsEditor } from './AutoReplySectionsEditor';
import { ChatLinkFields } from './ChatLinkFields';
import { ToggleRow } from './ToggleRow';
import { sendErrorMessages } from 'constants/errorStrings';

type Props = {
  assets: TAutoReplyMessageAsset[];
  assetsReady: boolean;
  chatLinkDisabled: boolean;
  onPreviewSectionsChange?: (sections: TMessageSection[]) => void;
  onPreviewChatLinkChange?: (
    chatLink: { text: string; label: string } | null,
  ) => void;
  onSaveSuccess: (message: string) => void;
  onSaveError: (message: string) => void;
};

export function KeywordReplyTab({
  assets,
  assetsReady,
  chatLinkDisabled,
  onPreviewSectionsChange,
  onPreviewChatLinkChange,
  onSaveSuccess,
  onSaveError,
}: Props) {
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
  const [ruleName, setRuleName] = useState('');
  const [ruleEnabled, setRuleEnabled] = useState(true);
  const [ruleChatLinkEnabled, setRuleChatLinkEnabled] = useState(false);
  const [ruleChatLinkText, setRuleChatLinkText] = useState('');
  const [ruleChatLinkLabel, setRuleChatLinkLabel] = useState('');
  const [ruleKeywords, setRuleKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [surveyImageUploading, setSurveyImageUploading] = useState(false);
  const [ruleSections, setRuleSections] = useState<TMessageSection[]>(() =>
    createDefaultAutoReplySections(),
  );
  const markRuleSaved = useUnsavedChangesGuard(
    JSON.stringify({
      ruleName,
      ruleEnabled,
      ruleChatLinkEnabled,
      ruleChatLinkText,
      ruleChatLinkLabel,
      ruleKeywords,
      keywordInput,
      ruleSections,
    }),
    surveyImageUploading,
  );

  const queryClient = useQueryClient();
  const { uploadToS3 } = useUploadFile();
  // アップロードの後に保存するため、送信先の組織を操作開始時に固定する
  const organizationId = useOrganizationId();
  const { toastError } = useToast();

  const { data: keywordRules = [] } = useQuery({
    queryKey: [queryKeys.AUTO_REPLY_KEYWORD_RULES],
    queryFn: getAutoReplyKeywordRules,
  });

  useEffect(() => {
    onPreviewSectionsChange?.(ruleSections);
  }, [onPreviewSectionsChange, ruleSections]);

  useEffect(() => {
    onPreviewChatLinkChange?.(
      !chatLinkDisabled && ruleChatLinkEnabled
        ? { text: ruleChatLinkText, label: ruleChatLinkLabel }
        : null,
    );
  }, [
    onPreviewChatLinkChange,
    chatLinkDisabled,
    ruleChatLinkEnabled,
    ruleChatLinkText,
    ruleChatLinkLabel,
  ]);

  const areRuleAssetsValid = areMessageAssetSectionsValid(
    ruleSections,
    assets,
    assetsReady,
  );

  const saveRuleMutation = useMutation({
    mutationFn: async () => {
      if (!areRuleAssetsValid) {
        throw new Error(sendErrorMessages.invalidAssetSelection);
      }
      if (!areSurveySectionsValid(ruleSections)) {
        throw new Error(sendErrorMessages.surveySelectionRequired);
      }
      if (!hasFilledReplySection(ruleSections)) {
        throw new Error(sendErrorMessages.noContent);
      }
      const formatted = await formatSectionsForAPI(ruleSections, {
        uploadToS3,
        onError: toastError,
      });
      if (formatted.length === 0) {
        throw new Error(sendErrorMessages.noContent);
      }
      const payload = {
        name: ruleName,
        enabled: ruleEnabled,
        keywords: ruleKeywords,
        sections: formatted,
        // プランで操作を止めていても保存値は維持する。実行時の抑止はbackend側で行う
        chatLinkEnabled: ruleChatLinkEnabled,
        chatLinkText: ruleChatLinkText.trim() || null,
        chatLinkLabel: ruleChatLinkLabel.trim() || null,
      };
      return editingRuleId
        ? updateAutoReplyKeywordRule(editingRuleId, payload, organizationId)
        : createAutoReplyKeywordRule(payload, organizationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.AUTO_REPLY_KEYWORD_RULES],
      });
      resetRuleForm();
      onSaveSuccess(autoReplyMessageStrings.messages.keywordSaveSuccess);
    },
    onError: (error: Error) => {
      onSaveError(error.message || autoReplyMessageStrings.messages.saveFailed);
    },
  });

  const deleteRuleMutation = useMutation({
    mutationFn: deleteAutoReplyKeywordRule,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.AUTO_REPLY_KEYWORD_RULES],
      });
      resetRuleForm();
      onSaveSuccess(autoReplyMessageStrings.messages.keywordDeleteSuccess);
    },
    onError: (error: Error) => {
      onSaveError(
        error.message || autoReplyMessageStrings.messages.deleteFailed,
      );
    },
  });

  const resetRuleForm = () => {
    const emptySections = createDefaultAutoReplySections();
    setEditingRuleId(null);
    setRuleName('');
    setRuleEnabled(true);
    setRuleChatLinkEnabled(false);
    setRuleChatLinkText('');
    setRuleChatLinkLabel('');
    setRuleKeywords([]);
    setKeywordInput('');
    setRuleSections(emptySections);
    onPreviewSectionsChange?.(emptySections);
    markRuleSaved();
  };

  const loadRule = (rule: TAutoReplyKeywordRule) => {
    setEditingRuleId(rule.id);
    setRuleName(rule.name);
    setRuleEnabled(rule.enabled);
    setRuleChatLinkEnabled(rule.chatLinkEnabled);
    setRuleChatLinkText(rule.chatLinkText ?? '');
    setRuleChatLinkLabel(rule.chatLinkLabel ?? '');
    setRuleKeywords(rule.keywords);
    setRuleSections(convertDraftSectionsToMessageSections(rule.sections));
    markRuleSaved();
  };

  const addKeyword = () => {
    const keyword = keywordInput.trim();
    if (!keyword || ruleKeywords.includes(keyword)) return;
    setRuleKeywords([...ruleKeywords, keyword]);
    setKeywordInput('');
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[340px_minmax(0,1fr)]">
      <div className="rounded-2xl border border-border/70 bg-white p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-semibold text-ink">登録済みキーワード応答</h2>
          <button
            type="button"
            onClick={resetRuleForm}
            className="shrink-0 whitespace-nowrap text-xs font-semibold text-accent"
          >
            {autoReplyMessageStrings.addKeywordRule}
          </button>
        </div>
        <div className="grid gap-2">
          {keywordRules.map((rule) => (
            <button
              key={rule.id}
              type="button"
              onClick={() => loadRule(rule)}
              className={`rounded-xl border p-3 text-left text-sm ${
                editingRuleId === rule.id
                  ? 'border-accent bg-accent-soft'
                  : 'border-border bg-white'
              }`}
            >
              <span className="block font-semibold">{rule.name}</span>
              <span className="text-xs text-muted">
                {rule.keywords.join(', ')}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4">
        <div className="rounded-2xl border border-border/70 bg-white p-4">
          <div className="grid gap-4">
            <ToggleRow
              checked={ruleEnabled}
              onChange={setRuleEnabled}
              label="ON/OFF"
            />
            <ChatLinkFields
              enabled={ruleChatLinkEnabled}
              text={ruleChatLinkText}
              buttonLabel={ruleChatLinkLabel}
              disabled={chatLinkDisabled}
              description={autoReplyMessageStrings.chatLink.keywordDescription}
              onEnabledChange={setRuleChatLinkEnabled}
              onTextChange={setRuleChatLinkText}
              onButtonLabelChange={setRuleChatLinkLabel}
            />
            <input
              value={ruleName}
              onChange={(event) => setRuleName(event.target.value)}
              placeholder={autoReplyMessageStrings.ruleNamePlaceholder}
              className="rounded-xl border border-border p-3 text-sm outline-none focus:border-accent"
            />
            <div>
              <input
                value={keywordInput}
                onChange={(event) => setKeywordInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addKeyword();
                  }
                }}
                placeholder={autoReplyMessageStrings.keywordPlaceholder}
                className="w-full rounded-xl border border-border p-3 text-sm outline-none focus:border-accent"
              />
              <div className="mt-2 flex flex-wrap gap-2">
                {ruleKeywords.map((keyword) => (
                  <button
                    key={keyword}
                    type="button"
                    onClick={() =>
                      setRuleKeywords(
                        ruleKeywords.filter((item) => item !== keyword),
                      )
                    }
                    className="rounded-full bg-ink px-3 py-1 text-xs text-white"
                  >
                    {keyword} ×
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <AutoReplySectionsEditor
          sections={ruleSections}
          assets={assets}
          onChange={setRuleSections}
          onUploadingChange={setSurveyImageUploading}
        />
        <div className="flex justify-between mx-4">
          <Button
            variant="outline"
            disabled={!editingRuleId || deleteRuleMutation.isPending}
            onClick={() =>
              editingRuleId && deleteRuleMutation.mutate(editingRuleId)
            }
          >
            削除
          </Button>
          <Button
            loading={saveRuleMutation.isPending}
            disabled={
              !ruleName.trim() ||
              ruleKeywords.length === 0 ||
              !areRuleAssetsValid ||
              surveyImageUploading
            }
            onClick={() => saveRuleMutation.mutate()}
          >
            {autoReplyMessageStrings.save}
          </Button>
        </div>
      </div>
    </div>
  );
}
